import { Button, ButtonDropdown, DataGridRef, TooltipV2 } from '@cimplico/components';
import { useRef, useState } from 'react';
import { useIsActiveSubscription } from '../../../../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../../../../hooks/use-is-firm-role';
import { Invitation } from '../../../../types/invitation';
import { useFirmContext } from '../../../../providers/FirmProvider';
import { useNavigate } from 'react-router-dom';
import SecondaryPageActions from '../../../../components/secondary-page-actions/SecondaryPageActions';
import FirmInvitationsSidePanel from './FirmInvitationsSidePanel';
import InvitationsTable from '../../../users/InvitationsTable';
import InvitationDeleteConfirmDialog from '../../../users/InvitationDeleteConfirmDialog';
import InvitationResendConfirmDialog from '../../../users/InvitationResendConfirmDialog';

function FirmInvitationsContainer(): JSX.Element {
    const { isManager, isAdmin } = useIsFirmRole();
    const { integrations, shortId } = useFirmContext();
    const navigate = useNavigate();
    const activeSubscription = useIsActiveSubscription();
    const fyiIntegration = Boolean(integrations.fyi?.cabinet);

    const tableRef = useRef<DataGridRef | null>(null);
    const roleCanInvite = isAdmin || isManager;

    const [action, setAction] = useState<string | undefined>();
    const [selectedInvitations, setSelectedInvitations] = useState<Invitation[]>([]);

    function redirectToNewUser(provider?: string): void {
        if (provider) {
            navigate(`/${shortId}/users/new?provider=${provider}`);
        } else {
            navigate(`/${shortId}/users/new`);
        }
    }

    function resetTable(): void {
        setSelectedInvitations([]);
        setAction(undefined);
        tableRef?.current?.clear();
    }

    return (
        <>
            <InvitationDeleteConfirmDialog
                invitations={selectedInvitations}
                isOpen={action === 'DELETE_INVITATION'}
                onClose={(): void => setAction(undefined)}
                onDelete={resetTable}
            />
            <InvitationResendConfirmDialog
                invitations={selectedInvitations}
                isOpen={action === 'RESEND_INVITATION'}
                onClose={(): void => setAction(undefined)}
                onResend={resetTable}
            />
            <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex flex-col gap-y-2 grow">
                    {selectedInvitations.length > 0 && roleCanInvite && (
                        <div className="ml-auto">
                            <SecondaryPageActions>
                                <Button
                                    type="danger"
                                    label={`Delete (${selectedInvitations.length})`}
                                    size="xs"
                                    onClick={(): void => setAction('DELETE_INVITATION')}
                                    disabled={!activeSubscription}
                                />
                                <Button
                                    type="secondary"
                                    label={`Re-send (${selectedInvitations.length})`}
                                    size="xs"
                                    onClick={(): void => setAction('RESEND_INVITATION')}
                                    disabled={!activeSubscription}
                                />
                            </SecondaryPageActions>
                        </div>
                    )}
                    {selectedInvitations.length === 0 && (
                        <div className="ml-auto">
                            <TooltipV2 message="This requires an admin or manager role." disabled={roleCanInvite} position="left">
                                <ButtonDropdown
                                    variant="primary"
                                    label="Add users"
                                    onClick={(): void => redirectToNewUser()}
                                    size="xs"
                                    disabled={!activeSubscription || !roleCanInvite}
                                >
                                    {fyiIntegration && (
                                        <ButtonDropdown.SecondaryAction
                                            label="Add from FYI"
                                            onClick={(): void => redirectToNewUser('fyi')}
                                            disabled={!activeSubscription}
                                        />
                                    )}
                                </ButtonDropdown>
                            </TooltipV2>
                        </div>
                    )}
                    <InvitationsTable tableRef={tableRef} onSelect={setSelectedInvitations} isSelectable={roleCanInvite} />
                </div>
                <FirmInvitationsSidePanel />
            </div>
        </>
    );
}

export default FirmInvitationsContainer;
